// Copyright (c) Docugami, Inc. All rights reserved.

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { loadTheme } from '@fluentui/react';
import { AppTheme, ThemeId } from './ThemeTypes';
import { docugamiTheme } from './themes/docugami';
import { blueSteelTheme } from './themes/bluesteel';

/**
 * Updates the favicon in the document head
 */
function updateFavicon(faviconUrl: string | undefined) {
    if (!faviconUrl) return;

    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
    if (link) {
        link.href = faviconUrl;
    }
}

/**
 * Updates the document title based on the theme
 */
function updateDocumentTitle(productName: string) {
    document.title = productName;
}

/**
 * Registry of available themes
 */
const themeRegistry: Record<ThemeId, AppTheme> = {
    docugami: docugamiTheme,
    bluesteel: blueSteelTheme,
};

/**
 * Get the default theme ID from environment or configuration
 */
function getDefaultThemeId(): ThemeId {
    // Check for theme override in localStorage
    const storedTheme = localStorage.getItem('app-theme') as ThemeId | null;
    if (storedTheme && themeRegistry[storedTheme]) {
        return storedTheme;
    }

    // Check for environment variable or config
    const envTheme = (window as any).__APP_THEME__ as ThemeId | undefined;
    if (envTheme && themeRegistry[envTheme]) {
        return envTheme;
    }

    // Default to docugami
    return 'docugami';
}

/**
 * Theme context value interface
 */
interface ThemeContextValue {
    /** Current active theme */
    theme: AppTheme;
    /** Current theme ID */
    themeId: ThemeId;
    /** Set the active theme by ID */
    setTheme: (themeId: ThemeId) => void;
    /** List of available theme IDs */
    availableThemes: ThemeId[];
    /** Get a theme by ID without setting it */
    getTheme: (themeId: ThemeId) => AppTheme | undefined;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Theme provider props
 */
interface ThemeProviderProps {
    children: ReactNode;
    /** Initial theme ID (optional, defaults to stored/environment theme or 'docugami') */
    initialThemeId?: ThemeId;
    /** Whether to persist theme choice to localStorage */
    persistTheme?: boolean;
}

/**
 * Theme provider component that wraps the application and provides theme context
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    initialThemeId,
    persistTheme = true,
}) => {
    const [themeId, setThemeId] = useState<ThemeId>(() => {
        return initialThemeId || getDefaultThemeId();
    });

    const theme = useMemo(() => {
        const selectedTheme = themeRegistry[themeId] || docugamiTheme;
        // Apply Fluent UI theme globally
        loadTheme(selectedTheme.fluentPrimaryTheme);
        return selectedTheme;
    }, [themeId]);

    // Update favicon and document title when theme changes
    useEffect(() => {
        updateFavicon(theme.logo.faviconUrl);
        updateDocumentTitle(theme.brand.productName);
    }, [theme]);

    const setTheme = useCallback((newThemeId: ThemeId) => {
        if (themeRegistry[newThemeId]) {
            setThemeId(newThemeId);
            if (persistTheme) {
                localStorage.setItem('app-theme', newThemeId);
            }
        } else {
            console.warn(`Theme "${newThemeId}" not found in registry`);
        }
    }, [persistTheme]);

    const getTheme = useCallback((id: ThemeId) => {
        return themeRegistry[id];
    }, []);

    const availableThemes = useMemo(() => Object.keys(themeRegistry) as ThemeId[], []);

    const contextValue = useMemo<ThemeContextValue>(() => ({
        theme,
        themeId,
        setTheme,
        availableThemes,
        getTheme,
    }), [theme, themeId, setTheme, availableThemes, getTheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};

/**
 * Hook to access the current theme and theme utilities
 */
export function useAppTheme(): ThemeContextValue {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useAppTheme must be used within a ThemeProvider');
    }
    return context;
}

/**
 * Hook to access just the current theme (for simpler use cases)
 */
export function useCurrentTheme(): AppTheme {
    const { theme } = useAppTheme();
    return theme;
}

/**
 * Hook to access theme colors directly
 */
export function useThemeColors() {
    const { theme } = useAppTheme();
    return theme.colors;
}

/**
 * Get the current default theme (for use in non-React contexts)
 */
export function getCurrentTheme(): AppTheme {
    return themeRegistry[getDefaultThemeId()] || docugamiTheme;
}
